import React from 'react';
import { Button, DatePicker, Input, Select, Space, Tooltip } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { EFFECTIVE_STATUS_FILTER_OPTIONS, FiltersState, LEVEL_FILTER_OPTIONS } from '../types';
import { AoiBizTagDictItem } from '../graphql/queries/listAoiBizTagDictionary';

interface Props {
    filters: FiltersState;
    onChange: (next: FiltersState) => void;
    onSearch: () => void;
    onReset: () => void;
    loading: boolean;
    /** 「飞到坐标」: 纯地图导航, 不查列表 (与「筛选」里把经纬度当条件是两回事, 见下方注释)。 */
    onFlyToCoord: (lat: number, lng: number) => void;
    /** 业务标签字典 (下拉选项); 未加载时传空数组, 下拉只剩占位。 */
    tagOptions?: AoiBizTagDictItem[];
}

/** 字段块: 标签在上、控件在下 (对齐产品原型的筛选区结构)。 */
const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
    label,
    required,
    children,
}) => (
    <div className="flex flex-col gap-1.5">
        {/* 标签用 antd Form 的口径 (14px / 500 / .88 黑): 比控件文字小一号会显得层级颠倒 */}
        <span className="text-sm font-medium" style={{ color: 'rgba(0,0,0,.88)' }}>
            {label}
            {required && <span style={{ color: '#ff4d4f' }}> *</span>}
        </span>
        {children}
    </div>
);

/**
 * 筛选区: ID / 名称 / 纬度经度 / Zipcode / 地址 / 创建时间 / 生效状态 / 级别 + 筛选·重置。
 *
 * <p>结构按产品原型 (标签在上), 控件与配色用 antd 默认 token, 与项目其他页面一致。
 * 「类型」本期不做 (后端与抽屉已预留 typeCode), 需要时把字段放回本行即可。
 *
 * <p>查询规则 (校验在页面层): 三选一 —— 填 Zipcode 可用全部模糊条件; 只填 AOI ID 走精确查;
 * 只选具体「生效状态」(未生效 / 已生效) 走跨分区查。
 *
 * <p><b>Zipcode 为空时, 名称 / 纬度 / 经度 / 地址 / 创建时间五个框一律置灰</b> (2026-09-21 契约收口)。
 * 跨分区路径唯一能用的索引前缀是 (bucket_no, 等值列), 进不去索引的条件只能退化成回表逐行过滤,
 * 代价与命中稀疏度成反比 ——「已生效 + 2 级」恒定 5 秒超时就是这么来的; 而名称 / 地址 / 经纬度这类
 * {@code LIKE '%kw%'} 是前置通配, 加任何索引都救不了。后端 (api-server + map-service) 两层都会拒,
 * 前端置灰只是让运营在点之前就知道, 而不是点完吃一个报错。
 * ID 框不置灰: 无 Zipcode 时它走的是主键精确查, 单点 lookup, 不受影响。
 */
const FilterBar: React.FC<Props> = ({ filters, onChange, onSearch, onReset, loading, onFlyToCoord, tagOptions = [] }) => {
    const patch = (p: Partial<FiltersState>) => onChange({ ...filters, ...p });

    /** 无 Zipcode = 走跨分区路径, 此时只有「生效状态」+「级别」+「ID 精确」可用, 其余置灰。 */
    const crossBucket = filters.zipcode.trim() === '';
    const crossBucketHint = '该筛选需填写 Zipcode';

    /**
     * 经纬度合法性 (WGS84 范围)。空值 / 非数字 / 越界都算不合法, 按钮置灰。
     * 纯前端判断, 不发请求。
     */
    const lat = Number(filters.lat.trim());
    const lng = Number(filters.lng.trim());
    const coordValid = filters.lat.trim() !== '' && filters.lng.trim() !== ''
        && Number.isFinite(lat) && Number.isFinite(lng)
        && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    const flyToCoord = () => {
        if (coordValid) {
            onFlyToCoord(lat, lng);
        }
    };
    const onEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    return (
        <div className="rounded-lg bg-white px-4 py-3 shadow-sm" style={{ flex: 'none' }}>
            <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
                {/* 无 Zipcode 时走主键精确查 (单点 lookup), 不受跨分区白名单约束, 所以不置灰, 只用 Tooltip 说清口径 */}
                <Field label="ID">
                    <Tooltip title={crossBucket ? '无 Zipcode 时按 AOI ID 精确查询 (不支持模糊)' : ''}>
                        <Input
                            value={filters.id}
                            onChange={(e) => patch({ id: e.target.value })}
                            onKeyDown={onEnter}
                            placeholder="AOI ID"
                            allowClear
                            style={{ width: 140 }}
                        />
                    </Tooltip>
                </Field>
                <Field label="名称">
                    <Tooltip title={crossBucket ? crossBucketHint : ''}>
                        <Input
                            value={filters.name}
                            onChange={(e) => patch({ name: e.target.value })}
                            onKeyDown={onEnter}
                            placeholder="名称关键词"
                            allowClear
                            disabled={crossBucket}
                            style={{ width: 170 }}
                        />
                    </Tooltip>
                </Field>
                {/*
                  经纬度不置灰: 它有两种用法, 「飞到坐标」是纯地图导航、不查列表, 无 Zipcode 时照样要能用。
                  只是"作为筛选条件"那一种在跨分区路径上不生效 (前端不下发, 后端也会拒), 用 Tooltip 说明。
                  标签不做动态文案: 边打 Zipcode 边变字会抖版。
                */}
                <Field label="纬度 / 经度">
                    {/*
                      两种用法共存 (2026-08-05 需求):
                      1) 点右侧定位按钮 (或在框内回车) → 地图直接飞到该坐标, 纯导航, 不查列表;
                      2) 点「筛选」→ 经纬度作为筛选条件参与查询 (定位建筑), 行为与之前一致。
                    */}
                    <Space.Compact>
                        {/* 跨分区时经纬度只用于定位, 不参与筛选 */}
                        <Input
                            value={filters.lat}
                            onChange={(e) => patch({ lat: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    flyToCoord();
                                }
                            }}
                            placeholder="34.05"
                            // 宽度要装得下 "-33.551709" 这类 10~11 字符的坐标, 否则输入时文字左滚, 看着像输不进去
                            style={{ width: 118 }}
                        />
                        <Input
                            value={filters.lng}
                            onChange={(e) => patch({ lng: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    flyToCoord();
                                }
                            }}
                            placeholder="-118.24"
                            style={{ width: 126 }}
                        />
                        <Tooltip title={coordValid ? '地图飞到该坐标 (不查询列表)' : '填写合法的纬度 / 经度'}>
                            <Button icon={<AimOutlined />} disabled={!coordValid} onClick={flyToCoord} />
                        </Tooltip>
                    </Space.Compact>
                </Field>
                <Field label="Zipcode">
                    <Input
                        value={filters.zipcode}
                        onChange={(e) => patch({ zipcode: e.target.value })}
                        onKeyDown={onEnter}
                        placeholder="90012"
                        allowClear
                        style={{ width: 120 }}
                    />
                </Field>
                <Field label="地址">
                    <Tooltip title={crossBucket ? crossBucketHint : ''}>
                        <Input
                            value={filters.address}
                            onChange={(e) => patch({ address: e.target.value })}
                            onKeyDown={onEnter}
                            placeholder="模糊匹配地址"
                            allowClear
                            disabled={crossBucket}
                            style={{ width: 200 }}
                        />
                    </Tooltip>
                </Field>
                <Field label="创建时间">
                    <DatePicker.RangePicker
                        value={[
                            filters.createdFrom != null ? dayjs(filters.createdFrom) : null,
                            filters.createdTo != null ? dayjs(filters.createdTo) : null,
                        ]}
                        onChange={(range) => patch({
                            createdFrom: range?.[0] ? range[0].startOf('day').valueOf() : null,
                            createdTo: range?.[1] ? range[1].endOf('day').valueOf() : null,
                        })}
                        placeholder={['开始日期', '结束日期']}
                        disabled={crossBucket}
                        style={{ width: 250 }}
                    />
                </Field>
                <Field label="生效状态">
                    {/*
                      人工审核视图 (2026-09-09 V2.1): 默认「全部」; 选「未生效」/「已生效」时后端按 AOI ID 升序、
                      前端单页收到 50 条 (AUDIT_LIST_PAGE_SIZE), 顺序与「全部」不同是刻意的。
                    */}
                    <Select
                        value={filters.effectiveStatus}
                        options={EFFECTIVE_STATUS_FILTER_OPTIONS}
                        onChange={(v) => patch({ effectiveStatus: v })}
                        style={{ width: 120 }}
                    />
                </Field>
                <Field label="级别">
                    {/* 审算法 2 级面时过滤掉同 zipcode 的几千栋 1 级楼; 与生效状态 / Zipcode 任意组合, 三条查询路径都生效 */}
                    <Select
                        value={filters.level}
                        options={LEVEL_FILTER_OPTIONS}
                        onChange={(v) => patch({ level: v })}
                        style={{ width: 100 }}
                    />
                </Field>
                <Field label="业务标签">
                    {/*
                      通用的标签精确筛 (不是某个场景专属的筛选项): 选项来自字典, 停用的标签也能筛 (已打的面要能找到)。
                      走 JSON_CONTAINS(biz_tags) + has_biz_tag 标志列, 只在 zipcode 分区路径上有索引可用, 跨分区置灰。
                    */}
                    <Tooltip title={crossBucket ? crossBucketHint : ''}>
                        <Select
                            value={filters.tagCode ?? undefined}
                            options={tagOptions.map((t) => ({
                                value: t.code,
                                label: t.enabled ? t.zhName : `${t.zhName} (已停用)`,
                            }))}
                            onChange={(v) => patch({ tagCode: v ?? null })}
                            placeholder="不筛选"
                            aria-label="业务标签"
                            allowClear
                            disabled={crossBucket}
                            style={{ width: 150 }}
                        />
                    </Tooltip>
                </Field>
                <div className="flex-1" />
                <Space>
                    <Button onClick={onReset}>重置</Button>
                    <Button type="primary" loading={loading} onClick={onSearch}>
                        筛选
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export default FilterBar;
